/**
 * CEFR Level Mapping Utility for IELTS Scores
 *
 * Maps IELTS band scores to CEFR (Common European Framework of Reference) levels
 * and provides utilities for language learning journey progression.
 */

// IELTS to CEFR mapping based on official IELTS/Cambridge guidelines
export const CEFR_LEVELS = {
  A1: {
    code: 'A1',
    name: 'Beginner',
    description:
      'Basic user who can understand and use familiar everyday expressions',
    color: '#f97316', // Orange
    ieltsMin: 0,
    ieltsMax: 2.5,
    gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
  },
  A2: {
    code: 'A2',
    name: 'Elementary',
    description: 'Can communicate in simple and routine tasks',
    color: '#eab308', // Yellow
    ieltsMin: 3,
    ieltsMax: 3.5,
    gradient: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
  },
  B1: {
    code: 'B1',
    name: 'Intermediate',
    description:
      'Can deal with most situations likely to arise while traveling',
    color: '#22c55e', // Green
    ieltsMin: 4,
    ieltsMax: 5,
    gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
  },
  B2: {
    code: 'B2',
    name: 'Upper Intermediate',
    description: 'Can interact with native speakers with a degree of fluency',
    color: '#3b82f6', // Blue
    ieltsMin: 5.5,
    ieltsMax: 6.5,
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  },
  C1: {
    code: 'C1',
    name: 'Advanced',
    description:
      'Can express ideas fluently and spontaneously without much searching for expressions',
    color: '#8b5cf6', // Purple
    ieltsMin: 7,
    ieltsMax: 8,
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
  },
  C2: {
    code: 'C2',
    name: 'Proficient',
    description: 'Can understand with ease virtually everything heard or read',
    color: '#ec4899', // Pink
    ieltsMin: 8.5,
    ieltsMax: 9,
    gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
  },
};

// Level order for progression
export const CEFR_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export const CEFR_TO_IELTS = {
  A1: '0.0 - 2.5',
  A2: '3.0 - 3.5',
  B1: '4.0 - 5.0',
  B2: '5.5 - 6.5',
  C1: '7.0 - 8.0',
  C2: '8.5 - 9.0',
};

export const CEFR_TO_DIFFICULTY = {
  A1: 1,
  A2: 2,
  B1: 3,
  B2: 4,
  C1: 5,
  C2: 5,
};

export const CEFR_TO_DIFFICULTY_RANGE = {
  A1: { min: 1, max: 2 },
  A2: { min: 1, max: 3 },
  B1: { min: 2, max: 4 },
  B2: { min: 3, max: 5 },
  C1: { min: 4, max: 5 },
  C2: { min: 5, max: 5 },
};

export const PRE_IELTS_LEVELS = ['A1', 'A2'];
export const ACADEMIC_ENTRY_LEVELS = ['B1', 'B2', 'C1', 'C2'];
export const FULL_MOCK_LEVELS = ['B2', 'C1', 'C2'];

export const formatIeltsRange = (levelCode) =>
  CEFR_TO_IELTS[levelCode] || '';

export const isPreIeltsLevel = (levelCode) =>
  PRE_IELTS_LEVELS.includes(levelCode);

export const canAccessFullMocks = (levelCode) =>
  FULL_MOCK_LEVELS.includes(levelCode);

export const canAccessAcademicTests = (levelCode) =>
  ACADEMIC_ENTRY_LEVELS.includes(levelCode);

/**
 * Get CEFR level from IELTS band score
 * @param {number} ieltsScore - IELTS band score (0-9)
 * @returns {object} CEFR level object
 */
export const getCEFRFromIELTS = (ieltsScore) => {
  if (ieltsScore === null || ieltsScore === undefined || isNaN(ieltsScore)) {
    return CEFR_LEVELS.A1;
  }

  const score = parseFloat(ieltsScore);

  if (score >= 8.5) return CEFR_LEVELS.C2;
  if (score >= 7) return CEFR_LEVELS.C1;
  if (score >= 5.5) return CEFR_LEVELS.B2;
  if (score >= 4) return CEFR_LEVELS.B1;
  if (score >= 3) return CEFR_LEVELS.A2;
  return CEFR_LEVELS.A1;
};

/**
 * Get the next CEFR level for progression
 * @param {string} currentLevel - Current CEFR level code (e.g., 'B1')
 * @returns {object|null} Next CEFR level object or null if at C2
 */
export const getNextCEFRLevel = (currentLevel) => {
  const currentIndex = CEFR_ORDER.indexOf(currentLevel);
  if (currentIndex === -1 || currentIndex === CEFR_ORDER.length - 1) {
    return null;
  }
  return CEFR_LEVELS[CEFR_ORDER[currentIndex + 1]];
};

/**
 * Get the previous CEFR level
 * @param {string} currentLevel - Current CEFR level code
 * @returns {object|null} Previous CEFR level object or null if at A1
 */
export const getPreviousCEFRLevel = (currentLevel) => {
  const currentIndex = CEFR_ORDER.indexOf(currentLevel);
  if (currentIndex <= 0) {
    return null;
  }
  return CEFR_LEVELS[CEFR_ORDER[currentIndex - 1]];
};

/**
 * Calculate days needed to progress from current level to target level
 * Based on 2 months (60 days) per level transition
 * @param {string} currentLevel - Current CEFR level code
 * @param {string} targetLevel - Target CEFR level code (default: C1)
 * @returns {number} Total days needed
 */
export const getDaysToTarget = (currentLevel, targetLevel = 'C1') => {
  const DAYS_PER_LEVEL = 60; // 2 months per level
  const currentIndex = CEFR_ORDER.indexOf(currentLevel);
  const targetIndex = CEFR_ORDER.indexOf(targetLevel);

  if (
    currentIndex === -1 ||
    targetIndex === -1 ||
    currentIndex >= targetIndex
  ) {
    return 0;
  }

  return (targetIndex - currentIndex) * DAYS_PER_LEVEL;
};

/**
 * Get progress percentage within current level
 * @param {number} ieltsScore - Current IELTS score
 * @returns {number} Progress percentage (0-100)
 */
export const getLevelProgress = (ieltsScore) => {
  const level = getCEFRFromIELTS(ieltsScore);
  const score = parseFloat(ieltsScore) || 0;

  const range = level.ieltsMax - level.ieltsMin;
  const progress = ((score - level.ieltsMin) / range) * 100;

  return Math.min(Math.max(progress, 0), 100);
};

/**
 * Get admission requirements information
 * @param {string} cefrLevel - Current CEFR level code
 * @returns {object} Admission info with eligibility and recommendations
 */
export const getAdmissionInfo = (cefrLevel) => {
  const admissionRequirements = {
    A1: {
      eligible: false,
      message: 'Most graduate programs require at least B2 level (IELTS 5.5+)',
      recommendation: 'Continue practicing to improve your English proficiency',
      programs: [],
    },
    A2: {
      eligible: false,
      message: 'Most graduate programs require at least B2 level (IELTS 5.5+)',
      recommendation:
        'Focus on building vocabulary and basic grammar structures',
      programs: [],
    },
    B1: {
      eligible: false,
      message: 'Most graduate programs require at least B2 level (IELTS 5.5+)',
      recommendation:
        'You are making progress! Keep practicing speaking and listening',
      programs: ['Some foundation/pathway programs'],
    },
    B2: {
      eligible: true,
      message: 'You meet the minimum requirements for many graduate programs',
      recommendation:
        "Aim for C1 (IELTS 7+) for top-tier graduate programs",
      programs: [
        "Most Master's programs",
        "MBA and specialized master's",
        'PhD programs (minimum)',
      ],
    },
    C1: {
      eligible: true,
      message:
        'Excellent! You meet requirements for competitive graduate programs',
      recommendation:
        "You're ready for top-tier programs. Maintain your level.",
      programs: [
        'Top graduate programs',
        'Ivy League/Russell Group universities',
        'Competitive scholarships',
      ],
    },
    C2: {
      eligible: true,
      message: 'Outstanding! You demonstrate native-like English proficiency',
      recommendation:
        'Your English is exceptional. Focus on other aspects of your application.',
      programs: [
        'All graduate programs',
        'Teaching assistantships',
        'English-medium research',
      ],
    },
  };

  return admissionRequirements[cefrLevel] || admissionRequirements.A1;
};

/**
 * Format CEFR level display string
 * @param {object} cefrLevel - CEFR level object
 * @returns {string} Formatted display string
 */
export const formatCEFRDisplay = (cefrLevel) => {
  return `${cefrLevel.code} - ${cefrLevel.name}`;
};

/**
 * Get CEFR badge color class
 * @param {string} levelCode - CEFR level code
 * @returns {string} CSS class name for badge styling
 */
export const getCEFRBadgeClass = (levelCode) => {
  const classMap = {
    A1: 'cefr-badge-a1',
    A2: 'cefr-badge-a2',
    B1: 'cefr-badge-b1',
    B2: 'cefr-badge-b2',
    C1: 'cefr-badge-c1',
    C2: 'cefr-badge-c2',
  };
  return classMap[levelCode] || 'cefr-badge-a1';
};

const cefrUtils = {
  CEFR_LEVELS,
  CEFR_ORDER,
  CEFR_TO_IELTS,
  CEFR_TO_DIFFICULTY,
  CEFR_TO_DIFFICULTY_RANGE,
  PRE_IELTS_LEVELS,
  ACADEMIC_ENTRY_LEVELS,
  FULL_MOCK_LEVELS,
  getCEFRFromIELTS,
  getNextCEFRLevel,
  getPreviousCEFRLevel,
  getDaysToTarget,
  getLevelProgress,
  getAdmissionInfo,
  formatCEFRDisplay,
  getCEFRBadgeClass,
  formatIeltsRange,
  isPreIeltsLevel,
  canAccessFullMocks,
  canAccessAcademicTests,
};

export default cefrUtils;
